import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Button } from 'react-bootstrap';

import { getValidLessonIndex } from '../../utils/getLessonIndex';
import Infohub from './Infohub';
import FancyButton from '../ui/fancyButton';
import LearningContextPreview from '../ui/LearningContextPreview';
import {
  selectActiveActivity,
  selectActivities,
  selectActiveActivityIndex,
  selectActiveCourse,
  selectActiveLesson,
  generateLessonPlan,
  nextActivity,
  prevActivity,
  clearLessonPlan,
  setActiveCourse,
  setActiveLesson,
  resetClassroomState,
  setPreviewLesson,
  syncUnitPagePosition,
} from '../../redux/classroomSlice';
import { startExam } from '../../redux/examSlice';
import { updateCourse } from '../../redux/coursesSlice';
import { selectProfile } from '../../redux/profileSlice';
import { useBackgroundPrefetcher } from '../../utils/useBackgroundPrefetcher';
import { clearContent } from '../../redux/contentSlice';
import Professor from '../Professor/Professor';
import Examiner from '../Examiner/Examiner';
import Librarian from '../Librarian/Librarian';

import './Classroom.css';

const parseDisplayTitle = (value, fallback = 'Untitled Lesson') => {
  if (!value) return fallback;

  try {
    const parsed = JSON.parse(value);
    return parsed?.title || fallback;
  } catch {
    return value;
  }
};

const normalizeObjectives = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const Classroom = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const profile = useSelector(selectProfile);
  const activeCourse = useSelector(selectActiveCourse);
  const rawActiveLesson = useSelector(selectActiveLesson);
  const previewLesson = useSelector((state) => state.classroom.previewLesson);
  const isPreviewLesson = useSelector((state) => state.classroom.isPreviewLesson);
  const selectedLesson = isPreviewLesson ? previewLesson : rawActiveLesson;

  const activities = useSelector(selectActivities);
  const activeActivity = useSelector(selectActiveActivity);
  const activeActivityIndex = useSelector(selectActiveActivityIndex);

  const { prefetchProgress } = useBackgroundPrefetcher(activeActivity?.id);
  const lessonParamsRef = useRef(null);
  const [isLessonReady, setIsLessonReady] = useState(false);
  const courseObjectives = useMemo(
    () => normalizeObjectives(activeCourse?.objectives),
    [activeCourse?.objectives]
  );
  const selectedLessonTitle = useMemo(
    () => parseDisplayTitle(selectedLesson?.title),
    [selectedLesson?.title]
  );
  const selectedLessonObjective = useMemo(
    () =>
      selectedLesson?.objective ||
      selectedLesson?.unit_objective ||
      'No lesson objective provided.',
    [selectedLesson?.objective, selectedLesson?.unit_objective]
  );

  const responsibleComponent = useMemo(() => {
    if (!activeActivity) return null;
    switch (activeActivity.leader) {
      case 'Professor':
        return <Professor key={activeActivity.id} />;
      case 'Examiner':
        return <Examiner key={activeActivity.id} />;
      case 'Librarian':
        return <Librarian key={activeActivity.id} />;
      default:
        return <Infohub />;
    }
  }, [activeActivity]);

  useEffect(() => {
    if (!activeCourse) {
      const storedCourses = JSON.parse(localStorage.getItem('courses') || '[]');
      const storedActiveId = localStorage.getItem('activeCourseId');
      const found = storedCourses.find((c) => c.id === storedActiveId);
      if (found) {
        dispatch(setActiveCourse(found));
      }
    }
  }, [activeCourse, dispatch]);

  useEffect(() => {
    if (activeCourse && !rawActiveLesson && !isPreviewLesson) {
      const index = activeCourse.currentLessonIndex ?? 0;
      const lesson = activeCourse.units?.[index];
      if (lesson) {
        dispatch(setActiveLesson(lesson));
      }
    }
  }, [activeCourse, rawActiveLesson, isPreviewLesson, dispatch]);

  useEffect(() => {
    if (!selectedLesson || lessonParamsRef.current) return;

    const lessonData = {
      courseId: activeCourse.id ?? null,
      title: activeCourse.title ?? 'Untitled Course',
      description: activeCourse.description ?? 'No course description provided.',
      difficultyLevel: activeCourse.difficultyLevel || activeCourse.difficulty_level || 'Unknown',
      courseObjectives: activeCourse.objectives || [],
      knowledgeGraph: activeCourse.knowledgeGraph || [],
      lessonId: selectedLesson.id ?? null,
      kgTopicId: selectedLesson.kgTopicId || selectedLesson.kg_topic_id || null,
      deepDivePrompt: selectedLesson.deepDivePrompt || selectedLesson.deep_dive_prompt || '',
      lessonTitle: selectedLesson.title ?? 'Untitled Lesson',
      lessonObjective:
        selectedLesson.objective ||
        selectedLesson.unit_objective ||
        'No lesson objective provided.',
      profileName: profile.name ?? 'Anonymous',
      educationLevel: profile.educationLevel ?? 'Not specified',
      careerGoals: profile.careerGoals ?? 'Not specified',
      interests: profile.interests ?? 'Not specified',
      certifications: profile.certifications ?? 'None',
    };

    lessonParamsRef.current = lessonData;
    dispatch(generateLessonPlan(lessonData));
  }, [selectedLesson, activeCourse, profile, dispatch, navigate]);

  useEffect(() => {
    if (selectedLesson && activities.length > 0 && activeActivity) {
      setIsLessonReady(true);
    }
  }, [selectedLesson, activities, activeActivity]);

  // Sync page position to unit in database when it changes
  useEffect(() => {
    if (selectedLesson?.id && activeActivityIndex >= 0 && !isPreviewLesson) {
      // Debounce the sync to avoid excessive database calls
      const timeoutId = setTimeout(() => {
        dispatch(
          syncUnitPagePosition({
            unitId: selectedLesson.id,
            pageIndex: activeActivityIndex,
          })
        );
      }, 1000); // Wait 1 second before syncing

      return () => clearTimeout(timeoutId);
    }
  }, [selectedLesson, activeActivityIndex, isPreviewLesson, dispatch]);

  const isLastActivity = activeActivityIndex === activities.length - 1;

  const handleEndSession = () => {
    if (!activeCourse || !selectedLesson || !activities) return;

    const totalLessons = activeCourse.units.length;
    const currentLessonIndex = activeCourse.currentLessonIndex ?? 0;
    if (!isPreviewLesson) {
      const isLastLesson = currentLessonIndex === totalLessons - 1;
      let updatedCourse;
      if (isLastLesson) {
        updatedCourse = {
          ...activeCourse,
          currentLessonIndex: currentLessonIndex, // Stay on last lesson
          progress: 100,
          completed: true,
          completedAt: '2025-06-29T11:27:20-07:00', // Source of truth time
        };
      } else {
        const nextLessonIndex = getValidLessonIndex(currentLessonIndex + 1, totalLessons);
        const lessonProgress = ((currentLessonIndex + 1) / totalLessons) * 100;
        updatedCourse = {
          ...activeCourse,
          currentLessonIndex: nextLessonIndex,
          progress: Math.round(lessonProgress),
          completed: false,
        };
      }
      dispatch(updateCourse(updatedCourse));
    }
    dispatch(setPreviewLesson(false));
    dispatch(clearLessonPlan());
    dispatch(resetClassroomState());
    dispatch(clearContent());
    dispatch(startExam());
    dispatch(setActiveCourse(null));
    dispatch(setPreviewLesson(null));
    lessonParamsRef.current = null;
    navigate('/dashboard');
  };

  if (!profile?.name) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Card className="shadow-sm p-4" style={{ maxWidth: '600px', width: '100%' }}>
          <Card.Body>
            <h4 className="mb-3">Profile Required</h4>
            <p>Please complete your student profile before accessing the classroom.</p>
            <FancyButton variant="primary" onClick={() => navigate('/profile')}>
              Go to Profile Setup
            </FancyButton>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!selectedLesson) {
    return (
      <Container className="classroom">
        <h2 className="classroom-title">No Active Lesson</h2>
        <p>There is no active lesson for this course.</p>
        <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
      </Container>
    );
  }

  if (!isLessonReady) {
    return (
      <Container className="classroom">
        <LearningContextPreview
          courseTitle={activeCourse?.title || 'Untitled Course'}
          lessonTitle={selectedLessonTitle}
          lessonObjective={selectedLessonObjective}
          courseObjectives={courseObjectives}
        />
        <div className="text-center mt-4">
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="classroom">
      <div className="classroom-header">
        <h1 className="lesson-title">{selectedLessonTitle}</h1>
      </div>

      {prefetchProgress &&
        prefetchProgress.running(
          <div className="prefetch-progress-bar">
            <p>
              Prefetching activities: {prefetchProgress.completed} / {prefetchProgress.total}
            </p>
            <div className="progress-bar-container">
              <div
                className="progress-bar-filled"
                style={{
                  width: `${(prefetchProgress.completed / prefetchProgress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

      <div className="classroom-content">
        <Card className="component-card">
          <Card.Body>
            <h3 className="component-title">{activeActivity.title}</h3>
            {responsibleComponent}
          </Card.Body>
        </Card>
      </div>

      <div className="navigation-controls">
        <FancyButton
          variant="secondary"
          onClick={() => dispatch(prevActivity())}
          disabled={activeActivityIndex === 0}
          className="nav-button"
        >
          Previous
        </FancyButton>
        {!isLastActivity ? (
          <FancyButton
            variant="secondary"
            onClick={() => dispatch(nextActivity())}
            className="nav-button"
          >
            Next
          </FancyButton>
        ) : (
          <FancyButton onClick={handleEndSession} variant="primary" className="end-session-button">
            End Session
          </FancyButton>
        )}
      </div>
    </Container>
  );
};

export default Classroom;
